import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, KpiCard, rm, LoadingButton } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { AlertTriangle, Wallet } from "lucide-react";
import FileUpload, { FileLink } from "@/components/FileUpload";

const TYPE_BADGE = { topup: "bg-emerald-100 text-emerald-800", expense: "bg-red-100 text-red-800", adjustment: "bg-blue-100 text-blue-800", opening: "bg-slate-100 text-slate-700" };
const STATUS_BADGE = { approved: "bg-emerald-100 text-emerald-800", pending: "bg-amber-100 text-amber-800", rejected: "bg-slate-200 text-slate-600" };

export default function PettyCash() {
  const { branches, canManage, hasRole } = useAuth();
  const canApprove = hasRole("super_admin", "management", "director", "branch_manager");
  const canConfigure = hasRole("super_admin", "management", "branch_manager");
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState("");
  const [ledger, setLedger] = useState(null);

  const loadAccounts = useCallback(async () => {
    try { const { data } = await api.get("/pettycash/accounts"); setAccounts(data); if (!selected && data.length) setSelected(data[0].branch_id); }
    catch (e) { toast.error(apiError(e)); }
  }, [selected]);
  const loadLedger = useCallback(async () => {
    if (!selected) return;
    try { const { data } = await api.get(`/pettycash/${selected}`); setLedger(data); } catch (e) { toast.error(apiError(e)); }
  }, [selected]);

  useEffect(() => { loadAccounts(); }, []);
  useEffect(() => { loadLedger(); }, [loadLedger]);

  const totalBalance = accounts.reduce((s, a) => s + (a.closing_balance || 0), 0);
  const lowCount = accounts.filter((a) => a.low_balance).length;

  // account settings form
  const acc = accounts.find((a) => a.branch_id === selected);
  const [setForm, setSetForm] = useState({ opening_balance: "", responsible_person: "", low_balance_threshold: "100" });
  useEffect(() => { if (acc) setSetForm({ opening_balance: String(acc.opening_balance || 0), responsible_person: acc.responsible_person || "", low_balance_threshold: String(acc.low_balance_threshold || 100) }); }, [acc]);
  const [setLoading, setSetLoading] = useState(false);
  const saveAccount = async () => {
    setSetLoading(true);
    try { await api.post("/pettycash/accounts", { branch_id: selected, opening_balance: Number(setForm.opening_balance || 0), responsible_person: setForm.responsible_person, low_balance_threshold: Number(setForm.low_balance_threshold || 0) });
      toast.success("Account saved"); loadAccounts(); loadLedger();
    } catch (e) { toast.error(apiError(e)); } finally { setSetLoading(false); }
  };

  // txn form
  const [tf, setTf] = useState({ type: "expense", date: todayISO(), amount: "", purpose: "", supplier: "", reference: "", receipt_url: "" });
  const [tfLoading, setTfLoading] = useState(false);
  const addTxn = async () => {
    if (!tf.amount) return toast.error("Amount required");
    setTfLoading(true);
    try { await api.post("/pettycash/transactions", { ...tf, branch_id: selected, amount: Number(tf.amount) });
      toast.success(tf.type === "expense" ? "Expense submitted (pending approval)" : "Transaction added"); setTf({ ...tf, amount: "", purpose: "", supplier: "", reference: "", receipt_url: "" }); loadLedger(); loadAccounts();
    } catch (e) { toast.error(apiError(e)); } finally { setTfLoading(false); }
  };
  const decide = async (id, decision) => {
    if (!window.confirm(`${decision === "approved" ? "Approve" : "Reject"} this expense?`)) return;
    try { await api.post(`/pettycash/transactions/${id}/approve?decision=${decision}`); toast.success(`Marked ${decision}`); loadLedger(); loadAccounts(); } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this petty cash transaction? This cannot be undone.")) return;
    try { await api.delete(`/pettycash/transactions/${id}`); toast.success("Deleted"); loadLedger(); loadAccounts(); } catch (e) { toast.error(apiError(e)); }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  return (
    <div>
      <PageHeader title="Petty Cash" subtitle="Per-branch ledger. Cash-sale top-ups flow in automatically from Cash Out. Expenses require approval before they reduce the balance." testid="pettycash-header" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard label="Total Petty Cash (all branches)" value={totalBalance} accent="blue" testid="kpi-petty-total" />
        <KpiCard label="Branches" value={accounts.length} isMoney={false} testid="kpi-petty-branches" />
        <KpiCard label="Low Balance Alerts" value={lowCount} isMoney={false} accent={lowCount ? "red" : "slate"} testid="kpi-petty-low" />
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Branch Ledger</div>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="petty-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
            <SelectContent>{accounts.map((a) => <SelectItem key={a.branch_id} value={a.branch_id}>{a.branch_name} · {rm(a.closing_balance)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        {accounts.map((a) => (
          <button key={a.branch_id} onClick={() => setSelected(a.branch_id)} data-testid={`petty-card-${a.branch_code}`}
            className={`text-left bg-white border rounded-sm p-4 transition-colors ${selected === a.branch_id ? "border-blue-500 ring-1 ring-blue-200" : "border-slate-200 hover:bg-slate-50"}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-800 flex items-center gap-1"><Wallet className="h-4 w-4 text-slate-400" /> {a.branch_name}</span>
              {a.low_balance && <Badge className="bg-red-100 text-red-800 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Low</Badge>}
            </div>
            <div className="text-2xl font-black tracking-tight mt-2 text-slate-900">{rm(a.closing_balance)}</div>
            <div className="text-[11px] text-slate-400 mt-1">Open {rm(a.opening_balance)} · Top-ups {rm(a.topups)} · Exp {rm(a.expenses)}</div>
          </button>
        ))}
      </div>

      {ledger && (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="space-y-4">
            {canConfigure && (
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Account Settings — {branchName(selected)}</h3>
                <div className="space-y-2">
                  <div><Label className="text-xs">Opening Balance</Label><Input type="number" value={setForm.opening_balance} onChange={(e) => setSetForm({ ...setForm, opening_balance: e.target.value })} data-testid="petty-opening" /></div>
                  <div><Label className="text-xs">Responsible Person</Label><Input value={setForm.responsible_person} onChange={(e) => setSetForm({ ...setForm, responsible_person: e.target.value })} data-testid="petty-responsible" /></div>
                  <div><Label className="text-xs">Low Balance Threshold</Label><Input type="number" value={setForm.low_balance_threshold} onChange={(e) => setSetForm({ ...setForm, low_balance_threshold: e.target.value })} data-testid="petty-threshold" /></div>
                </div>
                <LoadingButton loading={setLoading} onClick={saveAccount} className="bg-slate-900 hover:bg-slate-800 mt-3 w-full" data-testid="petty-save-account">Save Account</LoadingButton>
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">New Transaction</h3>
              <div className="space-y-2">
                <div><Label className="text-xs">Type</Label>
                  <Select value={tf.type} onValueChange={(v) => setTf({ ...tf, type: v })}>
                    <SelectTrigger data-testid="txn-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="expense">Expense</SelectItem>
                      {canApprove && <SelectItem value="topup">Top-up</SelectItem>}
                      {canApprove && <SelectItem value="adjustment">Adjustment</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Date</Label><Input type="date" value={tf.date} onChange={(e) => setTf({ ...tf, date: e.target.value })} data-testid="txn-date" /></div>
                  <div><Label className="text-xs">Amount</Label><Input type="number" value={tf.amount} onChange={(e) => setTf({ ...tf, amount: e.target.value })} data-testid="txn-amount" /></div>
                </div>
                <div><Label className="text-xs">Purpose</Label><Input value={tf.purpose} onChange={(e) => setTf({ ...tf, purpose: e.target.value })} data-testid="txn-purpose" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Supplier</Label><Input value={tf.supplier} onChange={(e) => setTf({ ...tf, supplier: e.target.value })} data-testid="txn-supplier" /></div>
                  <div><Label className="text-xs">Reference</Label><Input value={tf.reference} onChange={(e) => setTf({ ...tf, reference: e.target.value })} data-testid="txn-ref" /></div>
                </div>
                <FileUpload value={tf.receipt_url} onChange={(v) => setTf({ ...tf, receipt_url: v })} label="Receipt" testid="txn-receipt-upload" />
              </div>
              <LoadingButton loading={tfLoading} onClick={addTxn} className="bg-blue-600 hover:bg-blue-700 mt-3 w-full" data-testid="txn-save">Add Transaction</LoadingButton>
            </div>
          </div>

          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Ledger — {branchName(selected)}</h3>
              <span className="text-sm font-bold text-blue-700">Balance {rm(ledger.balance.closing_balance)}</span>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-3 px-4">Date</th><th className="py-3 px-4">Type</th><th className="py-3 px-4">Purpose</th><th className="py-3 px-4 text-right">Amount</th><th className="py-3 px-4">Status</th><th className="py-3 px-4"></th>
              </tr></thead>
              <tbody>
                {ledger.transactions.length === 0 ? <tr><td colSpan={6} className="text-center py-10 text-slate-400">No transactions yet.</td></tr> :
                  ledger.transactions.map((t) => (
                    <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`petty-txn-${t.id}`}>
                      <td className="py-2.5 px-4">{fmtDate(t.date)}</td>
                      <td className="py-2.5 px-4"><Badge className={TYPE_BADGE[t.type]}>{t.type}</Badge></td>
                      <td className="py-2.5 px-4"><div>{t.purpose || "—"}</div>{t.supplier && <div className="text-[11px] text-slate-400">{t.supplier}</div>}{t.linked_cash_movement_id && <div className="text-[11px] text-emerald-600">↳ from Cash Out</div>}{t.receipt_url && <div className="mt-0.5"><FileLink id={t.receipt_url} /></div>}</td>
                      <td className={`py-2.5 px-4 text-right tabular-nums font-medium ${(t.type === "expense" || (t.type === "adjustment" && t.amount < 0)) ? "text-red-600" : "text-emerald-700"}`}>{t.type === "expense" ? "-" : (t.amount >= 0 ? "+" : "")}{rm(t.amount)}</td>
                      <td className="py-2.5 px-4"><Badge className={STATUS_BADGE[t.approval_status]}>{t.approval_status}</Badge></td>
                      <td className="py-2.5 px-4 text-right whitespace-nowrap">
                        {t.approval_status === "pending" && canApprove && (<>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-600" onClick={() => decide(t.id, "approved")} data-testid={`approve-${t.id}`}>Approve</Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => decide(t.id, "rejected")} data-testid={`reject-${t.id}`}>Reject</Button>
                        </>)}
                        {!t.linked_cash_movement_id && canManage() && <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400" onClick={() => del(t.id)} data-testid={`del-txn-${t.id}`}>Delete</Button>}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
